import React, { useState } from "react";
import { Typography, Link, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from "@mui/material";

import { useGetSettingsQuery } from "../../store/apis/search";

export interface LastUpdatedProps {
  sectionName?: string;
}

const LastUpdated: React.FC<LastUpdatedProps> = ({ sectionName }) => {
  const { data = "" } = useGetSettingsQuery(sectionName);
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Temporary fix for about this database component
  const isAboutThisDatabaseVisible = sectionName === "spectrum_pricing";

  // Sample HTML content for the dialog - you can customize this based on your needs
  const dialogContent = `
    <div style="font-family: Arial, sans-serif; line-height: 1.6;padding:10px 20px;">
      <p>The database contains details of auctions of cellular spectrum since 2016. Please see below for an explanation of each field in the database. If you have any feedback or suggestions for new areas of data collection, please email the team at <a href="mailto:research@gsacom.com" style="color:rgb(47, 119, 234)">research@gsacom.com</a></p>
      
      <h2 style="color: #333; border-bottom: 1px solid #999; padding-bottom: 5px; margin-top: 14px;font-size:18px;">How to Use the Database</h2>
      <p>On the left is a filter pane which can narrow down the list of auction results by geography, frequency, technology, price and date. On the right is the results pane. The default results view shows all entries in the database in a table format ordered by the first column (country/territory). Click on "Bubble Chart" at the top to display a visualization of the current results or click on "Table" to return to the table view.</p>
      <p>In the table view, each row displays the overall result of one auction if the Operator is listed as "Total". If a detailed breakdown by operator is available, there will be an arrow on the left of the row next to the country. Click on this arrow to expand and show the operator details or to collapse the view and hide them.</p>
      <p>Columns can be toggled on or off using the tick boxes at the top of the table. Results in the table view can be ordered by each column by clicking on the column name. A down arrow indicates results in descending order; an up arrow indicates results in ascending order.</p>
      <p>To narrow the results, use the filters at the left side of the screen, then click the "Run search" button. Searches can be saved and recalled. Filters with selected items show a reminder like this  . Click the "Reset" button at the bottom of the filter pane to return all filters to their default state. The "Close all" button at the top of the filter pane closes expanded filters but retains their state.</p>
      
      <h2 style="color: #333; border-bottom: 1px solid #999; padding-bottom: 5px; margin-top: 14px;font-size:18px;">Fields and Filters</h2>
      <p><strong>ITU Region</strong> -  Regional telecom organisations. Selecting any grouping will include results from any of that organisation's member countries and territories.</p>
      
      <p><strong>Country/Territory</strong> -  Individual country or territories. Search for a country or territory by typing in the filter's search box. Text will be matched if it occurs anywhere in the name, so "kin" will find Burkina Faso and United Kingdom. Click on a name to select it. Repeat the process to select multiple countries or territories. Note that the spelling, presence or absence of a country or territory should not be inferred as an endorsement by the GSA.</p>
      <p><strong>Operator </strong> -  Shows "Total" if the result is for an entire auction, or the name of the network operator awarded an amount of spectrum. Click on the operator name to display the operator's details in the Operators database.</p>
      <p><strong>Licence</strong> -  The geography covered by the auction result: national, regional, national and regional, whole of territory, or multiple regions.</p>
      <p><strong>Frequenc</strong>y-  This filter is divided into three sections: low band, mid band and high band. Within each, frequencies are labelled with their ITU designations (b5, n66 and so on) or other labels if the band does not correspond to an ITU designation. Click "Band Explanation 5G" for a table showing details of 5G New Radio ("n") bands. Click "Band Explanation LTE" for a table showing details of LTE ("b") bands.<br/>
        Search for a band by typing in the filter's search box. Click on a band to select it. Repeat the process to select multiple bands. If multiple bands have been selected, click "Band control AND" to show only results that match all frequency band filters. Select "Band control OR" to show results that match any of the frequency band filters.</p>
      <p><strong>Technology</strong> -  The typical mobile technology generation deployed on these bands, either 5G and LTE.</p>
      <p><strong>Capacity (MHz)</strong> -  The quantity of spectrum capacity awarded.</p>
      <p><strong>Total Price</strong> -  The reported amount paid by an operator for the awarded spectrum or the total amount raised by an auction. The figure is listed in US dollars. </p>
      <p><strong>$/MHz/1000 pop.</strong> -  Price per megahertz per 1,000 population. This is a calculated measure, derived by dividing the total price field (in US dollars) by the capacity field (MHz) and the population of the country (in thousands). If the auction is for a region or part of a country/territory, rather than national, no figure is shown.</p>
      <p><strong>Completed Year</strong> - The year an auction was completed.</p>

      <h2 style="color: #333; border-bottom: 1px solid #999; padding-bottom: 5px; margin-top: 14px;font-size:18px;">Recent Updates</h2>
      <ul>
        <li>12 August 2025 -  Added "About this database" text to the database.</li>
        <li>24 June 2025 -  Added Bubble Chart visualization.</li>
      </ul>

    </div>
  `;

  return data ? (
    <>
      <Typography>
        {isAboutThisDatabaseVisible && (
          <>
            <Link
              component="button"
              onClick={handleOpen}
              sx={{
                textDecoration: "underline",
                cursor: "pointer",
                color: "primary.main",
                "&:hover": {
                  color: "primary.dark",
                },
              }}
            >
              About this database
            </Link>
            &nbsp; |{" "}
          </>
        )}
        Last updated:&nbsp;
        {data}
      </Typography>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "60vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#f5f5f5",
            fontSize: 20,
          }}
        >
          About the GAMBoD Spectrum Pricing Database
        </DialogTitle>
        <DialogContent sx={{ padding: 3 }}>
          <Box
            dangerouslySetInnerHTML={{ __html: dialogContent }}
            sx={{
              "& h1, & h3": {
                marginBottom: 0,
                fontSize: 16,
              },
              "& ul": {
                marginBottom: 0,
              },
              "& p": {
                marginTop: 0,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : (
    <></>
  );
};

export default LastUpdated;
